import React, { useEffect, useState } from 'react';

const ImageViewerModal = ({ isOpen, onClose, image, onNext, onPrev, hasNext, hasPrev, prevImage, nextImage }) => {
  const [loaded, setLoaded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${image.title || image.guestName || 'photo'}.webp`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } finally {
      setDownloading(false);
    }
  };

  // Reset loader whenever the image changes
  useEffect(() => {
    setLoaded(false);
  }, [image?.url]);

  // Preload adjacent images
  useEffect(() => {
    if (!isOpen) return;
    [prevImage?.url, nextImage?.url].forEach(url => {
      if (url) { const img = new Image(); img.src = url; }
    });
  }, [isOpen, prevImage, nextImage]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && hasNext) onNext();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrev, hasNext, hasPrev]);

  if (!isOpen || !image) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Top-right controls */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        {/* Download Button */}
        {!isIOS && <button
          onClick={handleDownload}
          disabled={downloading}
          className="text-white/70 hover:text-white p-2 disabled:opacity-50 transition-colors"
          title="Download photo"
        >
          {downloading ? (
            <svg className="animate-spin w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          )}
        </button>}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white p-2 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation Buttons */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-50 p-4 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-50 p-4 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Image Content */}
      <div className="relative z-40 max-w-[90vw] max-h-[90vh] flex flex-col items-center pointer-events-none">
        <div className="relative pointer-events-auto">
          {/* Spinner shown while full image loads */}
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center min-w-[200px] min-h-[200px]">
              <svg className="animate-spin h-10 w-10 text-white/60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          <img
            key={image.url}
            src={image.url}
            alt={image.title}
            className={`max-w-full max-h-[80vh] object-contain shadow-2xl rounded-sm transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
          />

          {/* Caption Overlay */}
          {loaded && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-12 text-white rounded-b-sm">
              {image.title && (
                <h3 className="text-xl font-['IBM_Plex_Serif'] font-medium mb-1">{image.title}</h3>
              )}
              <p className="text-sm font-light opacity-80">Uploaded by {image.guestName}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewerModal;

